import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { isFlightDetailsComplete, validateFlightDetails } from '../utils/flightValidation';
import { isQuoteSettingsComplete, validateQuoteSettings } from '../utils/quoteValidation';
import { formatUTCToDateTime } from '@/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';

/**
 * Custom hook for managing quote form state
 * Handles all form data, validation, and section management
 *
 * @returns {Object} Form state and handlers
 */
const useQuoteForm = () => {
  // Form data state
  const [formData, setFormData] = useState({
    // Client section
    client: {
      type: 'new',
      clientData: {}
    },

    // Flight section
    flights: {
      type: 'one-way',
      flights: [
        {
          departure: null,
          arrival: null,
          departureDate: '',
          returnDate: '',
          passengerCount: 1,
          notes: ''
        }
      ]
    },

    // Aircraft section
    aircraft: null,

    bidOptions: null,

    // Quote settings
    settings: {
      bidTitle: '',
      validUntil: '',
      expirationDate: '',
      notes: '',
      operator_price: '',
      broker_markup: '',
      final_price: ''
    }
  });

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    client: true,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Track which sections have been unlocked (by clicking Next)
  const [unlockedSections, setUnlockedSections] = useState({
    client: true,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Track sections marked complete by clicking Next
  const [manuallyCompleted, setManuallyCompleted] = useState({
    client: false,
    flights: false,
    aircraft: false,
    settings: false
  });

  // Calculate completed sections (form validation OR manually marked via Next)
  const completedSections = useMemo(() => {
    const clientComplete = isClientComplete(formData.client) || manuallyCompleted.client;
    const flightsComplete = isFlightDetailsComplete(formData.flights) || manuallyCompleted.flights;
    const aircraftComplete = !!formData.aircraft || manuallyCompleted.aircraft;
    const settingsComplete = isQuoteSettingsComplete(formData.settings) || manuallyCompleted.settings;

    return {
      client: clientComplete,
      flights: flightsComplete,
      aircraft: aircraftComplete,
      settings: settingsComplete
    };
  }, [formData, manuallyCompleted]);

  // Auto-expand next section when current is complete (DISABLED - using manual Next button instead)
  // useEffect(() => {
  //   if (completedSections.client && !expandedSections.flights && !completedSections.flights) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       client: false,
  //       flights: true
  //     }));
  //   } else if (completedSections.flights && !expandedSections.aircraft && !completedSections.aircraft) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       flights: false,
  //       aircraft: true
  //     }));
  //   } else if (completedSections.aircraft && !expandedSections.settings && !completedSections.settings) {
  //     setExpandedSections(prev => ({
  //       ...prev,
  //       aircraft: false,
  //       settings: true
  //     }));
  //   }
  // }, [completedSections, expandedSections]);

  // Track last auto-generated title so we can regenerate when flights change
  const autoTitleRef = useRef('');

  // Auto-generate bid title from flight data
  useEffect(() => {
    if (formData.flights?.flights?.length > 0) {
      const flights = formData.flights.flights;
      const firstFlight = flights[0];
      const departure = firstFlight.departure?.icao;
      const arrival = firstFlight.arrival?.icao;

      if (departure && arrival) {
        const flightType = formData.flights.type;
        let title = '';

        if (flightType === 'round-trip') {
          const legs = [departure, ...flights.map(f => f.arrival?.icao).filter(Boolean)];
          title = `Trip ${legs.join(' → ')}`;
        } else if (flightType === 'multi-leg') {
          const legs = [departure, ...flights.map(f => f.arrival?.icao).filter(Boolean)];
          title = `Trip ${legs.join(' → ')}`;
        } else {
          title = `${departure} → ${arrival}`;
        }

        // Only update if title is empty or matches the previous auto-generated title
        const currentTitle = formData.settings.bidTitle;
        if (!currentTitle || currentTitle === autoTitleRef.current) {
          autoTitleRef.current = title;
          setFormData(prev => ({
            ...prev,
            settings: {
              ...prev.settings,
              bidTitle: title
            }
          }));
        }
      }
    }
  }, [formData.flights]);

  // Update client section
  const updateClient = useCallback((clientData) => {
    setFormData(prev => ({
      ...prev,
      client: clientData
    }));
  }, []);

  // Update flights section
  const updateFlights = useCallback((flightsData) => {
    setFormData(prev => ({
      ...prev,
      flights: flightsData
    }));
  }, []);

  // Update aircraft selection
  const updateAircraft = useCallback((aircraft) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        aircraft: aircraft
      };
      return newData;
    });
  }, []);

  // Update settings section
  const updateSettings = useCallback((settings) => {
    setFormData(prev => ({
      ...prev,
      settings: settings
    }));
  }, []);

  // Update entire bidOptions array (for initialization)
  // Supports function updater: updateBidOptions(prev => newOptions)
  const updateBidOptions = useCallback((optionsOrUpdater) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: typeof optionsOrUpdater === 'function'
        ? optionsOrUpdater(prev.bidOptions)
        : optionsOrUpdater
    }));
  }, []);

  // Update a specific property for a specific bid option by its ID
  const updateBidOptionProperty = useCallback((optionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: prev.bidOptions?.map(option =>
        option.bid_option_id === optionId
          ? { ...option, [field]: value }
          : option
      ) || []
    }));
  }, []);

  // Update multiple properties for a specific bid option by its ID
  const updateBidOptionProperties = useCallback((optionId, updates) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: prev.bidOptions?.map(option =>
        option.bid_option_id === optionId
          ? { ...option, ...updates }
          : option
      ) || []
    }));
  }, []);

  // Remove a bid option by its ID
  const removeBidOption = useCallback((optionId) => {
    setFormData(prev => ({
      ...prev,
      bidOptions: prev.bidOptions?.filter(option => option.bid_option_id !== optionId) || []
    }));
  }, []);

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => {
      const isExpanding = !prev[section];
      if (isExpanding) {
        // Collapse all others, expand only the clicked one
        const newState = {};
        Object.keys(prev).forEach(key => { newState[key] = false; });
        newState[section] = true;
        return newState;
      }
      return { ...prev, [section]: false };
    });
  }, []);

  // Go to next section (collapse current, expand next, unlock next)
  const goToNextSection = useCallback((currentSection) => {
    const sectionOrder = ['client', 'flights', 'aircraft', 'settings'];
    const currentIndex = sectionOrder.indexOf(currentSection);

    if (currentIndex >= 0 && currentIndex < sectionOrder.length - 1) {
      const nextSection = sectionOrder[currentIndex + 1];
      setExpandedSections(prev => ({
        ...prev,
        [currentSection]: false,
        [nextSection]: true
      }));
      // Mark current section as complete
      setManuallyCompleted(prev => ({
        ...prev,
        [currentSection]: true
      }));
      // Unlock the next section
      setUnlockedSections(prev => ({
        ...prev,
        [nextSection]: true
      }));
    }
  }, []);

  // Go to previous section (collapse current, expand previous)
  const goToPreviousSection = useCallback((currentSection) => {
    const sectionOrder = ['client', 'flights', 'aircraft', 'settings'];
    const currentIndex = sectionOrder.indexOf(currentSection);
    
    if (currentIndex > 0) {
      const previousSection = sectionOrder[currentIndex - 1];
      setExpandedSections(prev => ({
        ...prev,
        [currentSection]: false,
        [previousSection]: true
      }));
    }
  }, []);

  // Check if entire form is complete (aircraft section is view-only, not required)
  const isFormComplete = useMemo(() => {
    const complete = (
      completedSections.client &&
      completedSections.flights &&
      completedSections.settings
    );
    return complete;
  }, [completedSections]);

  // Hydrate the form from an existing API quote (edit mode).
  // Mirrors the shape of the create-mode formData so the same section
  // components render correctly. Records source IDs under `_editing` so
  // the submit hook knows to PATCH instead of POST.
  //
  // `options.airports` is the cached list from useAirports — when passed,
  // each leg's airport is enriched with `name` / `city` so LocationInput
  // shows "KOPF - Miami-Opa Locka Exec" instead of just "KOPF".
  const loadFromQuote = useCallback((quote, { airports } = {}) => {
    if (!quote) return;
    const bid = quote.bid_items?.[0]?.bid;
    const options = bid?.bid_options || [];
    const legs = bid?.leg_itinerary || [];
    const firstOption = options[0] || {};

    const fallbackLeg = {
      departure_airport: quote.departure_airport,
      arrival_airport: quote.arrival_airport,
      departure_date: quote.departure_date,
      pax: quote.passenger_count,
    };

    const enrich = makeAirportEnricher(airports);
    const flightsList = (legs.length ? legs : [fallbackLeg]).map((leg) => legToFormFlight(leg, enrich));
    // If the quote is a round trip and only one leg is stored, synthesise the
    // return leg from quote.return_date so the broker can edit it.
    if (quote.is_round_trip && flightsList.length === 1 && quote.return_date) {
      const out = flightsList[0];
      // return_date is saved using the DEPARTURE airport's tz (see
      // resolveReturnDate), so hydrate it with the same tz to round-trip.
      const retTz = getAirportTimezone(quote.departure_airport);
      flightsList[0] = {
        ...out,
        returnDate: isoToDateObject(quote.return_date, retTz),
        returnTime: isoToTime(quote.return_date, retTz),
      };
    }

    const flightType = quote.is_multi_leg
      ? 'multi-leg'
      : quote.is_round_trip
        ? 'round-trip'
        : 'one-way';

    setFormData({
      _editing: {
        quote_id: quote.quote_id,
        bid_id: bid?.bid_id,
        bid_option_ids: options.map((o) => o.bid_option_id).filter(Boolean),
        source_status: (quote.status || '').toUpperCase(),
      },
      client: {
        type: 'existing',
        clientData: quote.client || {},
      },
      flights: {
        type: flightType,
        flights: flightsList,
      },
      aircraft: null,
      bidOptions: options.map(bidOptionToForm),
      settings: {
        bidTitle: bid?.trip_number ? `Trip ${bid.trip_number}` : '',
        validUntil: bid?.valid_until ? isoToDate(bid.valid_until) : '',
        expirationDate: bid?.valid_until ? isoToDate(bid.valid_until) : '',
        notes: quote.note_to_client || bid?.notes || '',
        ...pricingFieldsFrom(firstOption, quote),
      },
    });
    // Unlock and mark every section complete so the broker can jump
    // straight to whichever field they want to change.
    setExpandedSections({ client: true, flights: false, aircraft: false, settings: false });
    setUnlockedSections({ client: true, flights: true, aircraft: true, settings: true });
    setManuallyCompleted({ client: true, flights: true, aircraft: true, settings: true });
  }, []);

  // Derived flag — `true` once a quote has been hydrated (edit mode).
  const isEditing = !!formData._editing?.quote_id;

  // Reset form
  const resetForm = useCallback(({ preserveBidOptions } = {}) => {
    setFormData(prev => ({
      client: {
        type: 'new',
        clientData: {}
      },
      flights: {
        type: 'one-way',
        flights: [
          {
            departure: null,
            arrival: null,
            departureDate: '',
            returnDate: '',
            passengerCount: 1,
            notes: ''
          }
        ]
      },
      aircraft: null,
      bidOptions: preserveBidOptions ? prev.bidOptions : null,
      settings: {
        bidTitle: '',
        validUntil: '',
        expirationDate: '',
        notes: ''
      }
    }));
    setExpandedSections({
      client: true,
      flights: false,
      aircraft: false,
      settings: false
    });
    setUnlockedSections({
      client: true,
      flights: false,
      aircraft: false,
      settings: false
    });
    setManuallyCompleted({
      client: false,
      flights: false,
      aircraft: false,
      settings: false
    });
  }, []);

  // Get form errors (aircraft section is view-only, not validated)
  const getFormErrors = useCallback(() => {
    const errors = {};

    if (!completedSections.client) {
      errors.client = 'Client information is incomplete';
    }

    if (!completedSections.flights) {
      errors.flights = 'Flight details are incomplete';
    }

    return errors;
  }, [completedSections]);

  // Get the actual validation issues per section, BYPASSING `manuallyCompleted`.
  // `manuallyCompleted` is a UX shortcut to unlock subsequent sections; it
  // shouldn't gate the final preview/save. In edit mode every section starts
  // marked complete, so we MUST re-run the real validators here.
  //
  // Returns an array of { section, label, messages: [] } — one entry per
  // section that has at least one issue.
  const getValidationIssues = useCallback(() => {
    const issues = [];

    // ── Client ──────────────────────────────────────────────────────────────
    const clientMessages = getClientValidationMessages(formData.client);
    if (clientMessages.length) {
      issues.push({
        section: 'client',
        label: 'Client Information',
        messages: clientMessages,
      });
    }

    // ── Flights ─────────────────────────────────────────────────────────────
    const flightCheck = validateFlightDetails(formData.flights?.flights, formData.flights?.type);
    if (!flightCheck.isValid) {
      const messages = [];
      if (Array.isArray(flightCheck.errors)) {
        flightCheck.errors.forEach((legErrors, idx) => {
          if (!legErrors) return;
          Object.values(legErrors).forEach((msg) => {
            if (msg) messages.push(flightCheck.errors.length > 1 ? `Leg ${idx + 1}: ${msg}` : msg);
          });
        });
      }
      issues.push({
        section: 'flights',
        label: 'Flight Details',
        messages: messages.length ? messages : ['Flight details need updating.'],
      });
    }

    // ── Aircraft / bid options ──────────────────────────────────────────────
    // At least one option is required. Each remaining option needs an
    // aircraft_id and a non-zero final_price (a quote with $0 to the client
    // is almost certainly a bug, not intentional).
    const options = Array.isArray(formData.bidOptions) ? formData.bidOptions : [];
    if (options.length === 0) {
      issues.push({
        section: 'aircraft',
        label: 'Aircraft Options & Pricing',
        messages: ['Add at least one aircraft option before previewing the quote.'],
      });
    } else {
      const aircraftMessages = [];
      options.forEach((opt, idx) => {
        if (!opt?.aircraft_id) {
          aircraftMessages.push(`Option ${idx + 1}: aircraft is required.`);
        }
        const finalPrice = parseFloat(opt?.final_price);
        if (!finalPrice || finalPrice <= 0) {
          aircraftMessages.push(`Option ${idx + 1}: enter a final price greater than $0.`);
        }
      });
      if (aircraftMessages.length) {
        issues.push({
          section: 'aircraft',
          label: 'Aircraft Options & Pricing',
          messages: aircraftMessages,
        });
      }
    }

    // ── Settings ────────────────────────────────────────────────────────────
    const settingsErrors = validateQuoteSettings(formData.settings);
    if (Object.keys(settingsErrors).length > 0 || !isQuoteSettingsComplete(formData.settings)) {
      const messages = Object.values(settingsErrors).filter(Boolean);
      if (!messages.length && !isQuoteSettingsComplete(formData.settings)) {
        messages.push('Quote settings are incomplete.');
      }
      issues.push({
        section: 'settings',
        label: 'Quote Settings',
        messages,
      });
    }

    return issues;
  }, [formData]);

  // Force-open a specific section (vs `toggleSection`, which only toggles).
  // Used by the validation modal to jump directly to the section that needs
  // attention without flipping it back closed if it happened to already be open.
  const openSection = useCallback((section) => {
    setExpandedSections(prev => {
      const next = {};
      Object.keys(prev).forEach((key) => { next[key] = false; });
      next[section] = true;
      return next;
    });
    setUnlockedSections(prev => ({ ...prev, [section]: true }));
  }, []);

  return {
    formData,
    updateClient,
    updateFlights,
    updateAircraft,
    updateSettings,
    updateBidOptions,
    updateBidOptionProperty,
    updateBidOptionProperties,
    removeBidOption,
    toggleSection,
    goToNextSection,
    goToPreviousSection,
    expandedSections,
    unlockedSections,
    completedSections,
    isFormComplete,
    resetForm,
    getFormErrors,
    getValidationIssues,
    openSection,
    loadFromQuote,
    isEditing,
  };
};

/**
 * Build specific, user-facing messages describing which client fields are
 * missing or invalid. Returns [] when the client section is complete.
 * Used by the validation modal so brokers see "Email address is required"
 * instead of a generic "Client information is incomplete".
 */
const getClientValidationMessages = (client) => {
  const messages = [];
  if (!client || !client.clientData) {
    messages.push('Choose an existing client or enter a new one.');
    return messages;
  }

  const data = client.clientData;
  const firstName = (data.firstName || data.first_name || '').trim();
  const lastName = (data.lastName || data.last_name || '').trim();
  const email = (data.email || '').trim();

  if (!firstName) messages.push('First name is required.');
  if (!lastName) messages.push('Last name is required.');
  if (!email) {
    messages.push('Email address is required.');
  } else if (client.type === 'new' && !isValidEmail(email)) {
    // Existing clients came from the DB — we trust their stored email.
    messages.push('Email address is not in a valid format.');
  }

  return messages;
};

/**
 * Validates if client information is complete
 * @param {Object} client - Client data object
 * @returns {boolean}
 */
const isClientComplete = (client) => {
  if (!client || !client.clientData) {
    return false;
  }

  const data = client.clientData;

  // For existing client
  if (client.type === 'existing') {
    const email = data.email;
    const firstName = data.first_name || data.firstName;
    const lastName = data.last_name || data.lastName;
    return !!(email && firstName && lastName);
  }

  // For new client
  if (client.type === 'new') {
    return !!(
      data.firstName &&
      data.firstName.trim() !== '' &&
      data.lastName &&
      data.lastName.trim() !== '' &&
      data.email &&
      data.email.trim() !== '' &&
      isValidEmail(data.email)
    );
  }

  return false;
};

/**
 * Basic email validation
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ─── Edit-mode hydration helpers ─────────────────────────────────────────────
//
// These transform the API quote shape into the form's expected shape.
// Used exclusively by `loadFromQuote`.

// ISO string → "YYYY-MM-DD" for HTML <input type="date"> (settings.validUntil)
const isoToDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// UTC ISO → "HH:MM" in the airport's timezone. Must match how the value was
// saved (formatDateToISOWithTimezone interprets the input as airport-local) and
// displayed (formatUTCToDateTime renders in airport tz), otherwise the edit
// pre-fill is off by the airport↔browser offset.
const isoToTime = (iso, tz) => {
  if (!iso) return '';
  return formatUTCToDateTime(iso, tz, 'HH:mm') || '';
};

// UTC ISO → Date object carrying the airport-local calendar date. The custom
// DatePicker expects a real `Date`, and the save path reads the Date's
// *browser-local* Y/M/D — so we build the Date from the airport-local Y/M/D in
// browser-local time, making the round-trip (display → edit → save) stable.
const isoToDateObject = (iso, tz) => {
  if (!iso) return '';
  const ymd = formatUTCToDateTime(iso, tz, 'YYYY-MM-DD');
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? '' : date;
};

// Build an airport-enricher closure backed by the cached airport list.
// Returns `null` if no list provided — callers fall back to the bare {icao, code}.
const makeAirportEnricher = (airports) => {
  if (!Array.isArray(airports) || airports.length === 0) return null;
  // Index by both ICAO and IATA/FAA `code` so either-shape ICAO strings resolve.
  const byCode = new Map();
  for (const a of airports) {
    if (a?.icao) byCode.set(a.icao.toUpperCase(), a);
    if (a?.code) byCode.set(a.code.toUpperCase(), a);
  }
  return (icaoOrCode) => {
    if (!icaoOrCode) return null;
    return byCode.get(String(icaoOrCode).toUpperCase()) || null;
  };
};

// API leg → form flight shape. The form uses `departure: {icao}` / `arrival: {icao}`
// objects (LocationInput renders `${icao} - ${name}`) and stores
// `departureDate` as a Date object, `departureTime` as "HH:MM".
// When an `enrich` fn is provided, the airport object is hydrated with
// `name`, `city`, `country` from the cached airports list.
const legToFormFlight = (leg, enrich) => {
  const dep = leg?.departure_airport;
  const arr = leg?.arrival_airport;
  const tz = getAirportTimezone(dep);
  const enriched = (code) => {
    if (!code) return null;
    const full = enrich ? enrich(code) : null;
    return full
      ? { icao: full.icao || code, code: full.code || full.icao || code, name: full.name, city: full.city, country: full.country }
      : { icao: code, code };
  };
  return {
    departure: enriched(dep),
    arrival: enriched(arr),
    departureDate: isoToDateObject(leg?.departure_date, tz),
    departureTime: isoToTime(leg?.departure_date, tz),
    returnDate: '',
    returnTime: '',
    passengerCount: leg?.pax || 1,
    notes: leg?.notes || leg?.baggage_requirements || '',
  };
};

// API bid_option → form bid option shape. Preserve `bid_option_id` so the
// submit hook can PATCH the existing record instead of creating a new one.
const bidOptionToForm = (option) => ({
  ...option,
  bid_option_id: option.bid_option_id,
  aircraft_id: option.aircraft_id,
  operator_id: option.operator_id,
  total_price: option.total_price ?? '0.00',
  broker_markup: option.broker_markup ?? '0.00',
  final_price: option.final_price ?? '0.00',
  federal_excise_tax: option.federal_excise_tax ?? '0.00',
  segment_tax: option.segment_tax ?? '0.00',
  platform_fee: option.platform_fee ?? '0.00',
  operator_cost: option.operator_cost ?? '0.00',
  shortlisted: true,
});

// Pull the top-level pricing summary into settings. Prefer quote-level fields
// (these reflect the broker's last-saved totals) and fall back to the first
// option's fields for legacy quotes that didn't snapshot pricing at the quote.
const pricingFieldsFrom = (firstOption = {}, quote = {}) => ({
  operator_price: quote.operator_cost ?? firstOption.total_price ?? '',
  broker_markup: quote.broker_margin ?? firstOption.broker_markup ?? '',
  final_price: quote.total_due ?? firstOption.final_price ?? '',
  federal_excise_tax: quote.federal_excise_tax ?? firstOption.federal_excise_tax ?? '',
  segment_tax: quote.segment_tax ?? firstOption.segment_tax ?? '',
  platform_fee: quote.platform_fee ?? firstOption.platform_fee ?? '',
  operator_cost: quote.operator_cost ?? firstOption.operator_cost ?? '',
  taxable_amount: quote.taxable_amount ?? '',
});

export default useQuoteForm;
